import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  calculatePdfPagination,
  convertArabicNumbersToEnglish,
} from "../../../../shared/utils.js/utils";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FaEye, FaPrint, FaFileExcel, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import {
  getCostCenterReport,
  clearCostCenterReport,
  clearError,
  clearSuccess,
  getCostCenters,
} from "../../../../redux/Slices/AccountingReportsSlice";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import {
  generatePdfFromElementIds,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from "xlsx";
import i18n from "../../../../i18n/i18n";
import PdfPreviewModal from "./components/PdfPreviewModal";

// cost center report
const CostCenterReport = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  const {
    costCenterReport,
    costCenters,
    costCentersMeta,
    isLoading,
    error,
    success,
  } = useSelector(state => state.accountingReports);

  const [costCenterId, setCostCenterId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState("");
  const [costCenterPage, setCostCenterPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const SORT_FIELDS = { ACCOUNT: 'account_name', AMOUNT: 'amount', DATE: 'date', DESCRIPTION: 'description', DEBIT: 'debit', CREDIT: 'credit', BALANCE: 'balance' };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }} />;
    return sortOrder === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  useEffect(() => {
    setTitle("accountingReports.costCenterReportTitle", {
      defaultValue: "تقرير مراكز التكلفة",
    });
    dispatch(getCostCenters({ page: 1, per_page: 10 }));
    return () => {
      dispatch(clearCostCenterReport());
    };
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  // Debounce search
  useEffect(() => {
    let debounceTimer;
    debounceTimer = setTimeout(() => {
      if (costCenterSearchTerm) {
        setCostCenterPage(1);
        dispatch(
          getCostCenters({
            search: costCenterSearchTerm,
            page: 1,
            per_page: 10,
          }),
        );
      }
    }, 250);
    return () => clearTimeout(debounceTimer);
  }, [costCenterSearchTerm, dispatch]);

  // Listen to page changes only when not searching
  useEffect(() => {
    if (!costCenterSearchTerm) {
      dispatch(getCostCenters({ page: costCenterPage, per_page: 10 }));
    }
  }, [costCenterPage, costCenterSearchTerm, dispatch]);

  const allCostCenters = useMemo(() => {
    return Array.isArray(costCenters) ? costCenters : [];
  }, [costCenters]);

  const costCentersPagination =
    costCentersMeta || costCenters?.pagination || costCenters?.meta || null;

  // Remove redundant fetch on page change
  // ...existing code...

  useEffect(() => {
    if (
      costCentersPagination?.current_page &&
      costCentersPagination.current_page !== costCenterPage
    ) {
      setCostCenterPage(costCentersPagination.current_page);
    }
  }, [costCentersPagination?.current_page, costCenterPage]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        costCenterDropdownOpen &&
        !e.target.closest(".cost-center-dropdown-container")
      ) {
        setCostCenterDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [costCenterDropdownOpen]);

  const filteredCostCenters = useMemo(() => {
    if (!costCenterSearchTerm) return allCostCenters;
    const term = costCenterSearchTerm.toLowerCase();
    return allCostCenters.filter(costCenter => {
      const nameAr = costCenter.name_ar?.toLowerCase() || "";
      const nameEn = costCenter.name?.toLowerCase() || "";
      const code = costCenter.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) || nameEn.includes(term) || code.includes(term)
      );
    });
  }, [allCostCenters, costCenterSearchTerm]);

  const selectedCostCenter = allCostCenters.find(
    cc => cc.id === parseInt(costCenterId),
  );

  const handleSubmit = e => {
    e.preventDefault();
    if (!costCenterId) {
      toast.error(
        t("accountingReports.selectCostCenter", {
          defaultValue: "برجاء اختيار مركز التكلفة أولاً",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const params = { cost_center_id: costCenterId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    dispatch(getCostCenterReport(params));
  };

  const preparePdfPages = () => {
    const buildSectionPages = (sectionName, sectionItems) => {
      const topHeader = document.getElementById(
        `cost-center-report-pdf-measure-${sectionName}-top-header`,
      );
      const thead = document.getElementById(
        `cost-center-report-pdf-measure-${sectionName}-thead`,
      );
      const footer = document.getElementById(
        `cost-center-report-pdf-measure-${sectionName}-footer`,
      );
      const tbody = document.getElementById(
        `cost-center-report-pdf-measure-${sectionName}-tbody`,
      );
      const chunks = calculatePdfPagination(
        sectionItems,
        { topHeader, thead, footer, tbody },
        { safeMargin: 25 },
      );
      return chunks.map((chunk, idx) => ({
        section: sectionName,
        rows: chunk.items || chunk,
        startIndex: chunk.startIndex ?? idx * 10,
        isFirstSectionPage: idx === 0,
        isLastSectionPage: idx === chunks.length - 1,
      }));
    };

    let pages = [];

    if (sortedRevenues.length > 0 || sortedExpenses.length > 0) {
      const revenuePages = buildSectionPages("revenues", sortedRevenues);
      const expensePages = buildSectionPages("expenses", sortedExpenses);
      pages = [...revenuePages, ...expensePages];
    } else {
      pages = buildSectionPages("entries", sortedEntries);
    }

    if (pages.length === 0) {
      pages = [
        {
          section:
            sortedRevenues.length > 0 || sortedExpenses.length > 0
              ? "revenues"
              : "entries",
          rows: [],
          startIndex: 0,
          isFirstSectionPage: true,
          isLastSectionPage: true,
        },
      ];
    }

    return pages.map((page, pageIndex) => ({
      ...page,
      pageNumber: pageIndex + 1,
      isFirstPage: pageIndex === 0,
      isLastPage: pageIndex === pages.length - 1,
    }));
  };

  const handlePreviewAll = () => {
    if (!costCenterReport) {
      toast.error(
        t("accountingReports.noData", {
          defaultValue: "لا توجد بيانات للمعاينة",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const pages = preparePdfPages();
    setPdfPages(pages);
    setIsPreviewOpen(true);
  };

  const handlePrintAll = async () => {
    if (!costCenterReport) {
      toast.error(
        t("accountingReports.noData", {
          defaultValue: "لا توجد بيانات للطباعة",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      setPdfType("all");
      const pages = preparePdfPages();
      setPdfPages(pages);

      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = pages.map((_, i) =>
        document.getElementById(`cost-center-report-pdf-all-${i}`)
          ? `cost-center-report-pdf-all-${i}`
          : `preview-cost-center-report-pdf-all-${i}`,
      );
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_CostCenterReport_${new Date().toISOString().split("T")[0]}.pdf`,
        isRTL,
      });
      toast.success(
        t("accountingReports.printed", {
          defaultValue: "تم طباعة التقرير بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      toast.error(
        t("accountingReports.printError", {
          defaultValue: "حدث خطأ أثناء إنشاء PDF",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfPages([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!costCenterReport) {
      toast.error(
        t("accountingReports.noData", {
          defaultValue: "لا توجد بيانات للتصدير",
        }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();

      // Overview Sheet
      if (
        overview.total_revenue !== undefined ||
        overview.total_expense !== undefined ||
        overview.net_profit_loss !== undefined
      ) {
        const overviewData = [
          {
            [t("accountingReports.item", { defaultValue: "البند" })]: t(
              "accountingReports.totalRevenue",
              { defaultValue: "إجمالي الإيرادات" },
            ),
            [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
              overview.total_revenue || 0,
          },
          {
            [t("accountingReports.item", { defaultValue: "البند" })]: t(
              "accountingReports.totalExpenses",
              { defaultValue: "إجمالي المصروفات" },
            ),
            [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
              overview.total_expense || 0,
          },
          {
            [t("accountingReports.item", { defaultValue: "البند" })]: t(
              "accountingReports.netProfitLoss",
              { defaultValue: "صافي الربح/الخسارة" },
            ),
            [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
              overview.net_profit_loss || 0,
          },
        ];
        const overviewSheet = XLSX.utils.json_to_sheet(overviewData);
        XLSX.utils.book_append_sheet(
          workbook,
          overviewSheet,
          t("accountingReports.overview", { defaultValue: "نظرة عامة" }),
        );
      }

      // Revenues Sheet
      if (sortedRevenues.length > 0) {
        const revenuesData = sortedRevenues.map(item => ({
          [t("accountingReports.account", { defaultValue: "الحساب" })]:
            item.account_name || "",
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            item.amount || 0,
        }));
        revenuesData.push({
          [t("accountingReports.account", { defaultValue: "الحساب" })]: t(
            "accountingReports.total",
            { defaultValue: "الإجمالي" },
          ),
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            overview.total_revenue || 0,
        });
        const revenuesSheet = XLSX.utils.json_to_sheet(revenuesData);
        XLSX.utils.book_append_sheet(
          workbook,
          revenuesSheet,
          t("accountingReports.revenues", { defaultValue: "الإيرادات" }),
        );
      }

      // Expenses Sheet
      if (sortedExpenses.length > 0) {
        const expensesData = sortedExpenses.map(item => ({
          [t("accountingReports.account", { defaultValue: "الحساب" })]:
            item.account_name || "",
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            item.amount || 0,
        }));
        expensesData.push({
          [t("accountingReports.account", { defaultValue: "الحساب" })]: t(
            "accountingReports.total",
            { defaultValue: "الإجمالي" },
          ),
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            overview.total_expense || 0,
        });
        const expensesSheet = XLSX.utils.json_to_sheet(expensesData);
        XLSX.utils.book_append_sheet(
          workbook,
          expensesSheet,
          t("accountingReports.expenses", { defaultValue: "المصروفات" }),
        );
      }

      // Legacy Entries Sheet (for backward compatibility)
      if (
        sortedEntries.length > 0 &&
        sortedRevenues.length === 0 &&
        sortedExpenses.length === 0
      ) {
        const detailRows = sortedEntries.map(row => ({
          [t("accountingReports.date", { defaultValue: "التاريخ" })]:
            formatDate(row.date || row.transaction_date),
          [t("accountingReports.description", { defaultValue: "الوصف" })]:
            row.description || row.details || "",
          [t("accountingReports.debit", { defaultValue: "مدين" })]:
            row.debit ?? row.debits ?? 0,
          [t("accountingReports.credit", { defaultValue: "دائن" })]:
            row.credit ?? row.credits ?? 0,
          [t("accountingReports.balance", { defaultValue: "الرصيد" })]:
            row.balance ?? row.current_balance ?? row.running_balance ?? 0,
          [t("accountingReports.account", { defaultValue: "الحساب" })]:
            row.account_name || row.account || "",
        }));

        const totalsRow = {
          [t("accountingReports.date", { defaultValue: "التاريخ" })]: "",
          [t("accountingReports.description", { defaultValue: "الوصف" })]: t(
            "accountingReports.total",
            { defaultValue: "الإجمالي" },
          ),
          [t("accountingReports.debit", { defaultValue: "مدين" })]:
            totals.debit ?? totals.total_debit ?? 0,
          [t("accountingReports.credit", { defaultValue: "دائن" })]:
            totals.credit ?? totals.total_credit ?? 0,
          [t("accountingReports.balance", { defaultValue: "الرصيد" })]:
            totals.balance ?? totals.total_balance ?? 0,
          [t("accountingReports.account", { defaultValue: "الحساب" })]: "",
        };
        detailRows.push(totalsRow);

        const detailsSheet = XLSX.utils.json_to_sheet(detailRows);
        XLSX.utils.book_append_sheet(
          workbook,
          detailsSheet,
          t("accountingReports.costCenterReport", {
            defaultValue: "مراكز التكلفة",
          }),
        );
      }

      // Summary Sheet
      const summaryData = [
        {
          [t("accountingReports.item", { defaultValue: "البند" })]: t(
            "accountingReports.period",
            { defaultValue: "الفترة" },
          ),
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            `${formatDate(meta.start_date || startDate)} - ${formatDate(meta.end_date || endDate)}`,
        },
        {
          [t("accountingReports.item", { defaultValue: "البند" })]: t(
            "accountingReports.costCenter",
            { defaultValue: "مركز التكلفة" },
          ),
          [t("accountingReports.amount", { defaultValue: "المبلغ" })]:
            selectedCostCenter
              ? isRTL
                ? selectedCostCenter.name_ar || selectedCostCenter.name
                : selectedCostCenter.name || selectedCostCenter.name_ar
              : "—",
        },
      ];
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(
        workbook,
        summarySheet,
        t("accountingReports.summary", { defaultValue: "ملخص" }),
      );

      XLSX.writeFile(
        workbook,
        `CostCenterReport_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      toast.success(
        t("accountingReports.exported", {
          defaultValue: "تم تصدير البيانات بنجاح",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      toast.error(
        error.message ||
          t("accountingReports.exportError", {
            defaultValue: "فشل في تصدير البيانات",
          }),
        { rtl: isRTL },
      );
    }
  };

  const reportData = costCenterReport?.data || costCenterReport || {};
  const overview = reportData?.overview || {};
  const revenues = Array.isArray(reportData?.revenues)
    ? reportData.revenues
    : [];
  const expenses = Array.isArray(reportData?.expenses)
    ? reportData.expenses
    : [];
  const meta = reportData?.meta || {};
  const customerName =
    overview.customer_name ||
    overview.client_name ||
    meta.customer_name ||
    meta.client_name ||
    reportData.customer_name ||
    reportData.client_name ||
    (isRTL
      ? overview.customer?.name_ar ||
        overview.customer?.name_en ||
        meta.customer?.name_ar ||
        meta.customer?.name_en
      : overview.customer?.name_en ||
        overview.customer?.name_ar ||
        meta.customer?.name_en ||
        meta.customer?.name_ar) ||
    "";

  // For backward compatibility
  const entries = Array.isArray(reportData?.entries)
    ? reportData.entries
    : Array.isArray(reportData?.data)
      ? reportData.data
      : Array.isArray(reportData)
        ? reportData
        : [];
  const totals = reportData?.totals || reportData?.summary || {};

  const sortedRevenues = useMemo(() => {
    if (!sortField || !revenues.length) return revenues;
    const sortKey = sortField === 'account_name' || sortField === 'amount' ? sortField : 'account_name';
    return [...revenues].sort((a, b) => {
      let aVal = a[sortKey] ?? '';
      let bVal = b[sortKey] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [revenues, sortField, sortOrder]);

  const sortedExpenses = useMemo(() => {
    if (!sortField || !expenses.length) return expenses;
    const sortKey = sortField === 'account_name' || sortField === 'amount' ? sortField : 'account_name';
    return [...expenses].sort((a, b) => {
      let aVal = a[sortKey] ?? '';
      let bVal = b[sortKey] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [expenses, sortField, sortOrder]);

  const sortedEntries = useMemo(() => {
    if (!sortField || !entries.length) return entries;
    const sortKey = sortField;
    return [...entries].sort((a, b) => {
      let aVal = a[sortKey] ?? '';
      let bVal = b[sortKey] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [entries, sortField, sortOrder]);

  const formatNumber = value =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );

  const formatDate = dateString => {
    if (!dateString) return "—";
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    );
  };


  return (
    <div style={{ padding: "20px", direction: isRTL ? "rtl" : "ltr" }}>
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "24px",
          border: "1px solid #333",
        }}
      >
        <h2
          style={{
            color: "white",
            marginBottom: "20px",
            direction: dir,
            textAlign,
          }}
        >
          {t("accountingReports.costCenterReportTitle", {
            defaultValue: "تقرير مراكز التكلفة",
          })}
        </h2>

        {/* Filters */}
        <form
          onSubmit={handleSubmit}
          style={{
            backgroundColor: "#1a1f2e",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div
              className="cost-center-dropdown-container"
              style={{ position: "relative" }}
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  direction: dir,
                  textAlign,
                }}
              >
                {t("accountingReports.costCenter", {
                  defaultValue: "مركز التكلفة",
                })}{" "}
                <span style={{ color: "#dc3545" }}>*</span>
              </label>
              <div
                onClick={() => {
                  setCostCenterDropdownOpen(!costCenterDropdownOpen);
                  if (
                    !costCenterDropdownOpen &&
                    costCenterSearchInputRef.current
                  ) {
                    setTimeout(
                      () => costCenterSearchInputRef.current?.focus(),
                      100,
                    );
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  {selectedCostCenter
                    ? isRTL
                      ? selectedCostCenter.name_ar || selectedCostCenter.name
                      : selectedCostCenter.name || selectedCostCenter.name_ar
                    : t("accountingReports.selectCostCenter", {
                        defaultValue: "اختر مركز التكلفة",
                      })}
                </span>
                <span style={{ fontSize: "12px" }}>▼</span>
              </div>
              {costCenterDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "300px",
                    overflow: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <input
                    ref={costCenterSearchInputRef}
                    type="text"
                    placeholder={t("accountingReports.search", {
                      defaultValue: "بحث...",
                    })}
                    value={costCenterSearchTerm}
                    onChange={e => {
                      e.stopPropagation();
                      setCostCenterSearchTerm(e.target.value);
                    }}
                    onClick={e => e.stopPropagation()}
                    style={{
                      width: "100%",
                      padding: "10px",
                      backgroundColor: "#14263b",
                      border: "none",
                      borderBottom: "1px solid #333",
                      color: "white",
                      fontSize: "14px",
                    }}
                  />
                  {costCentersPagination &&
                    costCentersPagination.last_page >= 1 && (
                      <div
                        style={{
                          padding: "10px",
                          borderBottom: "1px solid #333",
                          background: "#14263b",
                          direction: dir,
                        }}
                      >
                        <div
                          style={{
                            color: "#ccc",
                            fontSize: 11,
                            marginBottom: 6,
                            textAlign: "center",
                            direction: dir,
                          }}
                        >
                          {t("accountingReports.page", {
                            defaultValue: "الصفحة",
                          })}{" "}
                          {costCentersPagination.current_page ||
                            costCenterPage ||
                            1}{" "}
                          {t("accountingReports.of", { defaultValue: "من" })}{" "}
                          {costCentersPagination.last_page || 1}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 4,
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              if (
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) > 1
                              ) {
                                setCostCenterPage(1);
                              }
                            }}
                            disabled={
                              (costCentersPagination.current_page ||
                                costCenterPage ||
                                1) === 1
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === 1
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === 1
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === 1
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                            title={t("accountingReports.firstPage", {
                              defaultValue: "الصفحة الأولى",
                            })}
                          >
                            ««
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              if (
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) > 1
                              ) {
                                setCostCenterPage(prev =>
                                  Math.max(1, prev - 1),
                                );
                              }
                            }}
                            disabled={
                              (costCentersPagination.current_page ||
                                costCenterPage ||
                                1) === 1
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === 1
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === 1
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === 1
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                            title={t("accountingReports.previous", {
                              defaultValue: "السابق",
                            })}
                          >
                            ‹
                          </button>
                          {(() => {
                            const pages = [];
                            const current =
                              costCentersPagination.current_page ||
                              costCenterPage ||
                              1;
                            const last = costCentersPagination.last_page || 1;
                            const showPages = 5;
                            let start = Math.max(
                              1,
                              current - Math.floor(showPages / 2),
                            );
                            let end = Math.min(last, start + showPages - 1);
                            if (end - start < showPages - 1) {
                              start = Math.max(1, end - showPages + 1);
                            }
                            for (let i = start; i <= end; i++) {
                              pages.push(i);
                            }
                            return pages.map(page => (
                              <button
                                key={page}
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  setCostCenterPage(page);
                                }}
                                style={{
                                  padding: "4px 8px",
                                  backgroundColor:
                                    page === current ? "#AC2000" : "#1a1f2e",
                                  color: "white",
                                  border:
                                    page === current
                                      ? "1px solid #AC2000"
                                      : "1px solid #444",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: 10,
                                  fontWeight:
                                    page === current ? "bold" : "normal",
                                  minWidth: "28px",
                                }}
                              >
                                {page}
                              </button>
                            ));
                          })()}
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              if (
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) < (costCentersPagination.last_page || 1)
                              ) {
                                setCostCenterPage(prev => prev + 1);
                              }
                            }}
                            disabled={
                              (costCentersPagination.current_page ||
                                costCenterPage ||
                                1) === (costCentersPagination.last_page || 1)
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === (costCentersPagination.last_page || 1)
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === (costCentersPagination.last_page || 1)
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === (costCentersPagination.last_page || 1)
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                            title={t("accountingReports.next", {
                              defaultValue: "التالي",
                            })}
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              const last = costCentersPagination.last_page || 1;
                              if (
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) < last
                              ) {
                                setCostCenterPage(last);
                              }
                            }}
                            disabled={
                              (costCentersPagination.current_page ||
                                costCenterPage ||
                                1) === (costCentersPagination.last_page || 1)
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === (costCentersPagination.last_page || 1)
                                  ? "#2a2a2a"
                                  : "#1a1f2e",
                              color:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === (costCentersPagination.last_page || 1)
                                  ? "#666"
                                  : "white",
                              border: "1px solid #444",
                              borderRadius: "4px",
                              cursor:
                                (costCentersPagination.current_page ||
                                  costCenterPage ||
                                  1) === (costCentersPagination.last_page || 1)
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 10,
                              fontWeight: "bold",
                              minWidth: "28px",
                            }}
                            title={t("accountingReports.lastPage", {
                              defaultValue: "الصفحة الأخيرة",
                            })}
                          >
                            »»
                          </button>
                        </div>
                      </div>
                    )}
                  <div>
                    {filteredCostCenters.length > 0 ? (
                      filteredCostCenters.map(costCenter => (
                        <div
                          key={costCenter.id}
                          onClick={() => {
                            setCostCenterId(costCenter.id.toString());
                            setCostCenterDropdownOpen(false);
                            setCostCenterSearchTerm("");
                          }}
                          style={{
                            padding: "10px",
                            cursor: "pointer",
                            color: "white",
                            borderBottom: "1px solid #333",
                            backgroundColor:
                              costCenterId === costCenter.id.toString()
                                ? "#AC2000"
                                : "transparent",
                          }}
                          onMouseEnter={e => {
                            if (costCenterId !== costCenter.id.toString()) {
                              e.currentTarget.style.backgroundColor = "#2a3441";
                            }
                          }}
                          onMouseLeave={e => {
                            if (costCenterId !== costCenter.id.toString()) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                        >
                          {isRTL
                            ? costCenter.name_ar || costCenter.name || ""
                            : costCenter.name || costCenter.name_ar || ""}{" "}
                          {costCenter.code ? `(${costCenter.code})` : ""}
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          padding: "10px",
                          color: "#999",
                          textAlign: "center",
                        }}
                      >
                        {t("accountingReports.noCostCenters", {
                          defaultValue: "لا توجد مراكز تكلفة",
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  direction: dir,
                  textAlign,
                }}
              >
                {t("accountingReports.startDate", {
                  defaultValue: "تاريخ البداية",
                })}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  direction: dir,
                  textAlign,
                }}
              >
                {t("accountingReports.endDate", {
                  defaultValue: "تاريخ النهاية",
                })}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? "#666" : "#AC2000",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "16px",
                opacity: isLoading ? 0.6 : 1,
                minWidth: "160px",
              }}
            >
              {isLoading
                ? t("accountingReports.loading", {
                    defaultValue: "جاري التحميل...",
                  })
                : t("accountingReports.view", { defaultValue: "عرض" })}
            </button>
          </div>
        </form>

        {/* Action Buttons */}
        {costCenterReport && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: "#444",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaEye />{" "}
              {t("accountingReports.previewAllPDF", {
                defaultValue: "عرض PDF للكل",
              })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
                opacity: isGeneratingPdf ? 0.6 : 1,
              }}
            >
              <FaPrint />{" "}
              {isGeneratingPdf
                ? t("accountingReports.generating", {
                    defaultValue: "جاري التوليد...",
                  })
                : t("accountingReports.printAllPDF", {
                    defaultValue: "طباعة PDF للكل",
                  })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaFileExcel />{" "}
              {t("accountingReports.exportExcel", {
                defaultValue: "تصدير Excel للكل",
              })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {costCenterReport && (
          <div
            style={{
              backgroundColor: "#1a1f2e",
              padding: "20px",
              borderRadius: "8px",
              marginTop: "20px",
            }}
          >
            <div
              style={{ color: "#ccc", marginBottom: "15px", direction: dir }}
            >
              <div style={{ textAlign }}>
                <strong>
                  {t("accountingReports.costCenter", {
                    defaultValue: "مركز التكلفة",
                  })}
                  :
                </strong>{" "}
                {selectedCostCenter
                  ? isRTL
                    ? selectedCostCenter.name_ar || selectedCostCenter.name
                    : selectedCostCenter.name || selectedCostCenter.name_ar
                  : "—"}
              </div>
              {customerName && (
                <div style={{ textAlign }}>
                  <strong>
                    {t("accountingReports.customer", {
                      defaultValue: "العميل",
                    })}
                    :
                  </strong>{" "}
                  {customerName}
                </div>
              )}
              <div style={{ textAlign }}>
                <strong>
                  {t("accountingReports.period", { defaultValue: "الفترة" })}:
                </strong>{" "}
                {formatDate(meta.start_date || startDate)} -{" "}
                {formatDate(meta.end_date || endDate)}
              </div>
            </div>

            {/* Overview Summary */}
            {(overview.total_revenue !== undefined ||
              overview.total_expense !== undefined ||
              overview.net_profit_loss !== undefined) && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "15px",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#111826",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid #333",
                    direction: dir,
                  }}
                >
                  <div
                    style={{
                      color: "#999",
                      fontSize: "14px",
                      marginBottom: "8px",
                      textAlign,
                    }}
                  >
                    {t("accountingReports.totalRevenue", {
                      defaultValue: "إجمالي الإيرادات",
                    })}
                  </div>
                  <div
                    style={{
                      color: "#28a745",
                      fontSize: "20px",
                      fontWeight: "bold",
                      textAlign,
                    }}
                  >
                    {formatNumber(overview.total_revenue || 0)}
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "#111826",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid #333",
                    direction: dir,
                  }}
                >
                  <div
                    style={{
                      color: "#999",
                      fontSize: "14px",
                      marginBottom: "8px",
                      textAlign,
                    }}
                  >
                    {t("accountingReports.totalExpenses", {
                      defaultValue: "إجمالي المصروفات",
                    })}
                  </div>
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "20px",
                      fontWeight: "bold",
                      textAlign,
                    }}
                  >
                    {formatNumber(overview.total_expense || 0)}
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "#111826",
                    padding: "15px",
                    borderRadius: "8px",
                    border: "1px solid #333",
                    direction: dir,
                  }}
                >
                  <div
                    style={{
                      color: "#999",
                      fontSize: "14px",
                      marginBottom: "8px",
                      textAlign,
                    }}
                  >
                    {t("accountingReports.netProfitLoss", {
                      defaultValue: "صافي الربح/الخسارة",
                    })}
                  </div>
                  <div
                    style={{
                      color:
                        (overview.net_profit_loss || 0) >= 0
                          ? "#28a745"
                          : "#dc3545",
                      fontSize: "20px",
                      fontWeight: "bold",
                      textAlign,
                    }}
                  >
                    {formatNumber(overview.net_profit_loss || 0)}
                  </div>
                </div>
              </div>
            )}

            {/* Revenues Section */}
            {sortedRevenues.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <h3
                  style={{
                    color: "#fff",
                    marginBottom: "15px",
                    fontSize: "18px",
                    fontWeight: "bold",
                    direction: dir,
                    textAlign,
                  }}
                >
                  {t("accountingReports.revenues", {
                    defaultValue: "الإيرادات",
                  })}
                </h3>
                <div
                  style={{
                    overflowX: "auto",
                    backgroundColor: "#111826",
                    borderRadius: "8px",
                    padding: "12px",
                    direction: dir,
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      color: "#eee",
                      fontSize: "13px",
                      direction: dir,
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#252f3f" }}>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign: "center",
                            width: "36px",
                          }}
                        >
                          #
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('account_name')}
                        >
                          {t("accountingReports.account", {
                            defaultValue: "الحساب",
                          })}{getSortIcon('account_name')}
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign: "center",
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('amount')}
                        >
                          {t("accountingReports.amount", {
                            defaultValue: "المبلغ",
                          })}{getSortIcon('amount')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRevenues.map((item, idx) => (
                        <tr
                          key={item.account_id || idx}
                          style={{ borderBottom: "1px solid #222" }}
                        >
                          <td style={{ padding: "10px", textAlign: "center" }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding: "10px", textAlign }}>
                            {item.account_name || "—"}
                          </td>
                          <td
                            style={{
                              padding: "10px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}
                          >
                            {formatNumber(item.amount || 0)}
                          </td>
                        </tr>
                      ))}
                      <tr
                        style={{
                          backgroundColor: "#1f2937",
                          fontWeight: "bold",
                        }}
                      >
                        <td style={{ padding: "10px", textAlign }} colSpan={2}>
                          {t("accountingReports.total", {
                            defaultValue: "الإجمالي",
                          })}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {formatNumber(overview.total_revenue || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Expenses Section */}
            {sortedExpenses.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <h3
                  style={{
                    color: "#fff",
                    marginBottom: "15px",
                    fontSize: "18px",
                    fontWeight: "bold",
                    direction: dir,
                    textAlign,
                  }}
                >
                  {t("accountingReports.expenses", {
                    defaultValue: "المصروفات",
                  })}
                </h3>
                <div
                  style={{
                    overflowX: "auto",
                    backgroundColor: "#111826",
                    borderRadius: "8px",
                    padding: "12px",
                    direction: dir,
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      color: "#eee",
                      fontSize: "13px",
                      direction: dir,
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#252f3f" }}>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign: "center",
                            width: "36px",
                          }}
                        >
                          #
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('account_name')}
                        >
                          {t("accountingReports.account", {
                            defaultValue: "الحساب",
                          })}{getSortIcon('account_name')}
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign: "center",
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('amount')}
                        >
                          {t("accountingReports.amount", {
                            defaultValue: "المبلغ",
                          })}{getSortIcon('amount')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedExpenses.map((item, idx) => (
                        <tr
                          key={item.account_id || idx}
                          style={{ borderBottom: "1px solid #222" }}
                        >
                          <td style={{ padding: "10px", textAlign: "center" }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding: "10px", textAlign }}>
                            {item.account_name || "—"}
                          </td>
                          <td
                            style={{
                              padding: "10px",
                              textAlign: "center",
                              fontWeight: "bold",
                            }}
                          >
                            {formatNumber(item.amount || 0)}
                          </td>
                        </tr>
                      ))}
                      <tr
                        style={{
                          backgroundColor: "#1f2937",
                          fontWeight: "bold",
                        }}
                      >
                        <td style={{ padding: "10px", textAlign }} colSpan={2}>
                          {t("accountingReports.total", {
                            defaultValue: "الإجمالي",
                          })}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {formatNumber(overview.total_expense || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Legacy Entries Display (for backward compatibility) */}
            {sortedEntries.length > 0 &&
              sortedRevenues.length === 0 &&
              sortedExpenses.length === 0 && (
                <div
                  style={{
                    overflowX: "auto",
                    backgroundColor: "#111826",
                    borderRadius: "8px",
                    padding: "12px",
                    direction: dir,
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      color: "#eee",
                      fontSize: "13px",
                      direction: dir,
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#252f3f" }}>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign: "center",
                            width: "36px",
                          }}
                        >
                          #
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('date')}
                        >
                          {t("accountingReports.date", {
                            defaultValue: "التاريخ",
                          })}{getSortIcon('date')}
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign,
                          }}
                        >
                          {t("accountingReports.description", {
                            defaultValue: "الوصف",
                          })}
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign: "center",
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('debit')}
                        >
                          {t("accountingReports.debit", {
                            defaultValue: "مدين",
                          })}{getSortIcon('debit')}
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign: "center",
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('credit')}
                        >
                          {t("accountingReports.credit", {
                            defaultValue: "دائن",
                          })}{getSortIcon('credit')}
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign: "center",
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('balance')}
                        >
                          {t("accountingReports.balance", {
                            defaultValue: "الرصيد",
                          })}{getSortIcon('balance')}
                        </th>
                        <th
                          style={{
                            borderBottom: "1px solid #333",
                            padding: "10px",
                            textAlign,
                          }}
                        >
                          {t("accountingReports.account", {
                            defaultValue: "الحساب",
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedEntries.map((row, idx) => {
                        const debit = row.debit ?? row.debits ?? 0;
                        const credit = row.credit ?? row.credits ?? 0;
                        const balance =
                          row.balance ??
                          row.current_balance ??
                          row.running_balance ??
                          0;
                        return (
                          <tr
                            key={row.id || row.entry_id || idx}
                            style={{ borderBottom: "1px solid #222" }}
                          >
                            <td style={{ padding: "10px", textAlign: "center" }}>
                              {idx + 1}
                            </td>
                            <td style={{ padding: "10px", textAlign }}>
                              {formatDate(row.date || row.transaction_date)}
                            </td>
                            <td style={{ padding: "10px", textAlign }}>
                              {row.description || row.details || "—"}
                            </td>
                            <td
                              style={{ padding: "10px", textAlign: "center" }}
                            >
                              {formatNumber(debit)}
                            </td>
                            <td
                              style={{ padding: "10px", textAlign: "center" }}
                            >
                              {formatNumber(credit)}
                            </td>
                            <td
                              style={{
                                padding: "10px",
                                textAlign: "center",
                                fontWeight: "bold",
                              }}
                            >
                              {formatNumber(balance)}
                            </td>
                            <td style={{ padding: "10px", textAlign }}>
                              {row.account_name || row.account || "—"}
                            </td>
                          </tr>
                        );
                      })}
                      <tr
                        style={{
                          backgroundColor: "#1f2937",
                          fontWeight: "bold",
                        }}
                      >
                        <td style={{ padding: "10px", textAlign }} colSpan={3}>
                          {t("accountingReports.total", {
                            defaultValue: "الإجمالي",
                          })}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {formatNumber(
                            totals.debit ?? totals.total_debit ?? 0,
                          )}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {formatNumber(
                            totals.credit ?? totals.total_credit ?? 0,
                          )}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {formatNumber(
                            totals.balance ?? totals.total_balance ?? 0,
                          )}
                        </td>
                        <td style={{ padding: "10px", textAlign }}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

            {/* No Data Message */}
            {sortedEntries.length === 0 &&
              sortedRevenues.length === 0 &&
              sortedExpenses.length === 0 && (
                <div
                  style={{
                    padding: "30px",
                    textAlign: "center",
                    color: "#9ca3af",
                  }}
                >
                  {t("accountingReports.noData", {
                    defaultValue: "لا توجد بيانات",
                  })}
                </div>
              )}
          </div>
        )}
      </div>

      {/* PDF Print Templates (one full page per chunk) */}
      {pdfType === "all" &&
        costCenterReport &&
        pdfPages &&
        pdfPages.length > 0 &&
        pdfPages.map((page, pageIndex) => (
          <CostCenterReportPdfPage
            key={pageIndex}
            idPrefix="cost-center-report-pdf-all"
            page={page}
            pageIndex={pageIndex}
            isRTL={isRTL}
            dir={dir}
            t={t}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            selectedCostCenter={selectedCostCenter}
            customerName={customerName}
            meta={meta}
            startDate={startDate}
            endDate={endDate}
            overview={overview}
            totals={totals}
            formatNumber={formatNumber}
            formatDate={formatDate}
            hidden
          />
        ))}

      <PdfPreviewModal
        isOpen={isPreviewOpen && !!costCenterReport}
        title={t("accountingReports.costCenterReportTitle", {
          defaultValue: "تقرير مراكز التكلفة",
        })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
        contentWidth="794px"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {pdfPages &&
            pdfPages.length > 0 &&
            pdfPages.map((page, pageIndex) => (
              <CostCenterReportPdfPage
                key={pageIndex}
                idPrefix="preview-cost-center-report-pdf-all"
                page={page}
                pageIndex={pageIndex}
                isRTL={isRTL}
                dir={dir}
                t={t}
                publicUrl={publicUrl}
                headerLogo={headerLogo}
                selectedCostCenter={selectedCostCenter}
                customerName={customerName}
                meta={meta}
                startDate={startDate}
                endDate={endDate}
                overview={overview}
                totals={totals}
                formatNumber={formatNumber}
                formatDate={formatDate}
              />
            ))}
        </div>
      </PdfPreviewModal>

      {/* Hidden measurement templates used to calculate dynamic PDF pagination.
          Revenues/expenses/entries are conceptually separate tables, so each gets
          its own measurement set. The "top header" mock-up for whichever section
          renders first also includes the overview summary cards markup (with
          placeholder numbers) so pagination reserves that space on page 1 for
          every section, since calculatePdfPagination assumes a uniform header
          height across pages of the same section run. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "794px",
          pointerEvents: "none",
          visibility: "hidden",
        }}
      >
        {/* Revenues measurement set */}
        <div id="cost-center-report-pdf-measure-revenues-top-header" style={{ width: "100%" }}>
          <img
            src={`${publicUrl}/HeaderAce.png`}
            alt=""
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              marginBottom: "8px",
              borderBottom: "4px double #000",
              paddingBottom: "8px",
            }}
          />
          <div style={{ marginTop: "12px", textAlign: "center", marginBottom: "20px" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", margin: 0 }}>
              {t("accountingReports.costCenterReportTitle", { defaultValue: "تقرير مراكز التكلفة" })}
            </h2>
            <div style={{ fontSize: "14px", marginTop: "8px" }}>x</div>
          </div>
          {/* Reserve space for the first-page-only overview summary cards */}
          <div style={{ marginBottom: "20px", fontSize: "12px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "15px" }}>
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>x</td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>0.00</td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>x</td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>0.00</td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>x</td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>0.00</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "10px", textAlign: "center" }}>x</h3>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
          <thead id="cost-center-report-pdf-measure-revenues-thead">
            <tr>
              <th style={{ border: "1px solid #000", padding: "6px" }}>#</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.account", { defaultValue: "الحساب" })}</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.amount", { defaultValue: "المبلغ" })}</th>
            </tr>
          </thead>
          <tbody id="cost-center-report-pdf-measure-revenues-tbody">
            {revenues.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{idx + 1}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{item.account_name || "—"}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{item.amount || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="cost-center-report-pdf-measure-revenues-footer" style={{ height: "120px" }} />

        {/* Expenses measurement set (no overview cards mock-up: revenues section, if present, always precedes expenses so page 1 belongs to revenues) */}
        <div id="cost-center-report-pdf-measure-expenses-top-header" style={{ width: "100%" }}>
          <img
            src={`${publicUrl}/HeaderAce.png`}
            alt=""
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              marginBottom: "8px",
              borderBottom: "4px double #000",
              paddingBottom: "8px",
            }}
          />
          <div style={{ marginTop: "12px", textAlign: "center", marginBottom: "20px" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", margin: 0 }}>
              {t("accountingReports.costCenterReportTitle", { defaultValue: "تقرير مراكز التكلفة" })}
            </h2>
            <div style={{ fontSize: "14px", marginTop: "8px" }}>x</div>
          </div>
          {/* Reserve space for the overview summary cards in case expenses end up being the first section (revenues empty) */}
          {revenues.length === 0 && (
            <div style={{ marginBottom: "20px", fontSize: "12px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "15px" }}>
                <tbody>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>x</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>0.00</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>x</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>0.00</td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>x</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>0.00</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "10px", textAlign: "center" }}>x</h3>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
          <thead id="cost-center-report-pdf-measure-expenses-thead">
            <tr>
              <th style={{ border: "1px solid #000", padding: "6px" }}>#</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.account", { defaultValue: "الحساب" })}</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.amount", { defaultValue: "المبلغ" })}</th>
            </tr>
          </thead>
          <tbody id="cost-center-report-pdf-measure-expenses-tbody">
            {expenses.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{idx + 1}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{item.account_name || "—"}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{item.amount || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="cost-center-report-pdf-measure-expenses-footer" style={{ height: "120px" }} />

        {/* Legacy entries measurement set (used only when both revenues and expenses are empty) */}
        <div id="cost-center-report-pdf-measure-entries-top-header" style={{ width: "100%" }}>
          <img
            src={`${publicUrl}/HeaderAce.png`}
            alt=""
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              marginBottom: "8px",
              borderBottom: "4px double #000",
              paddingBottom: "8px",
            }}
          />
          <div style={{ marginTop: "12px", textAlign: "center", marginBottom: "20px" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", margin: 0 }}>
              {t("accountingReports.costCenterReportTitle", { defaultValue: "تقرير مراكز التكلفة" })}
            </h2>
            <div style={{ fontSize: "14px", marginTop: "8px" }}>x</div>
          </div>
          <div style={{ marginBottom: "20px", fontSize: "12px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "15px" }}>
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>x</td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>0.00</td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>x</td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>0.00</td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>x</td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>0.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
          <thead id="cost-center-report-pdf-measure-entries-thead">
            <tr>
              <th style={{ border: "1px solid #000", padding: "6px" }}>#</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.date", { defaultValue: "التاريخ" })}</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.description", { defaultValue: "الوصف" })}</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.debit", { defaultValue: "مدين" })}</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.credit", { defaultValue: "دائن" })}</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.balance", { defaultValue: "الرصيد" })}</th>
              <th style={{ border: "1px solid #000", padding: "6px" }}>{t("accountingReports.account", { defaultValue: "الحساب" })}</th>
            </tr>
          </thead>
          <tbody id="cost-center-report-pdf-measure-entries-tbody">
            {entries.slice(0, 5).map((row, idx) => (
              <tr key={idx}>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{idx + 1}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{row.date || "—"}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{row.description || row.details || "—"}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{row.debit || row.debits || 0}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{row.credit || row.credits || 0}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{row.balance || 0}</td>
                <td style={{ border: "1px solid #000", padding: "6px" }}>{row.account_name || row.account || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="cost-center-report-pdf-measure-entries-footer" style={{ height: "120px" }} />
      </div>
    </div>
  );
};

const CostCenterReportPdfPage = ({
  idPrefix,
  page,
  pageIndex,
  isRTL,
  dir,
  t,
  publicUrl,
  headerLogo,
  selectedCostCenter,
  customerName,
  meta,
  startDate,
  endDate,
  overview,
  totals,
  formatNumber,
  formatDate,
  hidden = false,
}) => {
  const rows = page.rows || [];
  const showOverview =
    pageIndex === 0 &&
    (overview.total_revenue !== undefined ||
      overview.total_expense !== undefined ||
      overview.net_profit_loss !== undefined);

  return (
    <div
      id={`${idPrefix}-${pageIndex}`}
      aria-hidden={hidden || undefined}
      style={{
        position: hidden ? "absolute" : "static",
        left: hidden ? "-9999px" : undefined,
        top: hidden ? 0 : undefined,
        width: "794px",
        minHeight: "1123px",
        height: "auto",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: "bold",
        padding: "40px",
        boxSizing: "border-box",
        overflow: "visible",
        boxShadow: hidden ? undefined : "0 4px 20px rgba(0, 0, 0, 0.5)",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
          width: "100%",
          minHeight: "1043px",
          height: "auto",
          overflow: "visible",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            minHeight: "1033px",
            height: "1033px",
            padding: "18px 18px 10px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: "bold",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            direction: isRTL ? "rtl" : "ltr",
            backgroundColor: "#ffffff",
            boxSizing: "border-box",
            width: "100%",
            overflow: "visible",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              height: "600px",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "95%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.1,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: "100%",
            }}
          >
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "8px",
                borderBottom: "4px double #000",
                paddingBottom: "8px",
                flexShrink: 0,
              }}
              crossOrigin="anonymous"
            />

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
                minHeight: 0,
                paddingBottom: "120px",
              }}
            >
              {pageIndex === 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    textAlign: "center",
                    marginBottom: "20px",
                    direction: dir,
                  }}
                >
                  <h2
                    style={{
                      fontSize: "20px",
                      fontWeight: "700",
                      color: "#000080",
                      margin: 0,
                      direction: dir,
                    }}
                  >
                    {t("accountingReports.costCenterReportTitle", {
                      defaultValue: "تقرير مراكز التكلفة",
                    })}
                  </h2>
                  <div style={{ fontSize: "14px", marginTop: "8px", direction: dir }}>
                    {selectedCostCenter
                      ? isRTL
                        ? selectedCostCenter.name_ar || selectedCostCenter.name || ""
                        : selectedCostCenter.name || selectedCostCenter.name_ar || ""
                      : "—"}{" "}
                    - {formatDate(meta.start_date || startDate)} -{" "}
                    {formatDate(meta.end_date || endDate)}
                    {customerName
                      ? ` | ${t("accountingReports.customer", { defaultValue: "العميل" })}: ${customerName}`
                      : ""}
                  </div>
                </div>
              )}

              {showOverview && (
                <div style={{ marginBottom: "20px", fontSize: "12px" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginBottom: "15px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "8px",
                            textAlign: isRTL ? "right" : "left",
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          {t("accountingReports.totalRevenue", { defaultValue: "إجمالي الإيرادات" })}
                        </td>
                        <td style={{ border: "1px solid #000", padding: "8px", textAlign: "center", fontWeight: "bold" }}>
                          {formatNumber(overview.total_revenue || 0)}
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "8px",
                            textAlign: isRTL ? "right" : "left",
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          {t("accountingReports.totalExpenses", { defaultValue: "إجمالي المصروفات" })}
                        </td>
                        <td style={{ border: "1px solid #000", padding: "8px", textAlign: "center", fontWeight: "bold" }}>
                          {formatNumber(overview.total_expense || 0)}
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "8px",
                            textAlign: isRTL ? "right" : "left",
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          {t("accountingReports.netProfitLoss", { defaultValue: "صافي الربح/الخسارة" })}
                        </td>
                        <td style={{ border: "1px solid #000", padding: "8px", textAlign: "center", fontWeight: "bold" }}>
                          {formatNumber(overview.net_profit_loss || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {page.isFirstSectionPage && (
                <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "10px", textAlign: "center" }}>
                  {page.section === "revenues"
                    ? t("accountingReports.revenues", { defaultValue: "الإيرادات" })
                    : page.section === "expenses"
                      ? t("accountingReports.expenses", { defaultValue: "المصروفات" })
                      : t("accountingReports.costCenterReport", { defaultValue: "مراكز التكلفة" })}
                </h3>
              )}

              {(page.section === "revenues" || page.section === "expenses") && (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "11px",
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          backgroundColor: "#e5e7eb",
                          textAlign: "center",
                          width: "36px",
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          backgroundColor: "#e5e7eb",
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {t("accountingReports.account", { defaultValue: "الحساب" })}
                      </th>
                      <th
                        style={{
                          border: "1px solid #000",
                          padding: "6px",
                          backgroundColor: "#e5e7eb",
                          textAlign: "center",
                        }}
                      >
                        {t("accountingReports.amount", { defaultValue: "المبلغ" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ border: "1px solid #000", padding: "8px", textAlign: "center" }}>
                          {t("accountingReports.noData", { defaultValue: "لا توجد بيانات" })}
                        </td>
                      </tr>
                    )}
                    {rows.map((item, idx) => (
                      <tr key={item.account_id || idx}>
                        <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>
                          {page.startIndex + idx + 1}
                        </td>
                        <td style={{ border: "1px solid #000", padding: "6px", textAlign: isRTL ? "right" : "left" }}>
                          {item.account_name || "—"}
                        </td>
                        <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center", fontWeight: "bold" }}>
                          {formatNumber(item.amount || 0)}
                        </td>
                      </tr>
                    ))}
                    {page.isLastSectionPage && (
                      <tr style={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
                        <td style={{ border: "1px solid #000", padding: "8px", textAlign: isRTL ? "right" : "left" }} colSpan={2}>
                          {t("accountingReports.total", { defaultValue: "الإجمالي" })}
                        </td>
                        <td style={{ border: "1px solid #000", padding: "8px", textAlign: "center" }}>
                          {formatNumber(
                            page.section === "revenues" ? overview.total_revenue || 0 : overview.total_expense || 0,
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {page.section === "entries" && (
                <div style={{ color: "#000", fontSize: "13px" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "12px",
                      direction: isRTL ? "rtl" : "ltr",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={{ border: "1px solid #000", padding: "6px", backgroundColor: "#e5e7eb", textAlign: "center", width: "36px" }}>#</th>
                        <th style={{ border: "1px solid #000", padding: "6px", backgroundColor: "#e5e7eb", textAlign: isRTL ? "right" : "left" }}>
                          {t("accountingReports.date", { defaultValue: "التاريخ" })}
                        </th>
                        <th style={{ border: "1px solid #000", padding: "6px", backgroundColor: "#e5e7eb", textAlign: isRTL ? "right" : "left" }}>
                          {t("accountingReports.description", { defaultValue: "الوصف" })}
                        </th>
                        <th style={{ border: "1px solid #000", padding: "6px", backgroundColor: "#e5e7eb", textAlign: "center" }}>
                          {t("accountingReports.debit", { defaultValue: "مدين" })}
                        </th>
                        <th style={{ border: "1px solid #000", padding: "6px", backgroundColor: "#e5e7eb", textAlign: "center" }}>
                          {t("accountingReports.credit", { defaultValue: "دائن" })}
                        </th>
                        <th style={{ border: "1px solid #000", padding: "6px", backgroundColor: "#e5e7eb", textAlign: "center" }}>
                          {t("accountingReports.balance", { defaultValue: "الرصيد" })}
                        </th>
                        <th style={{ border: "1px solid #000", padding: "6px", backgroundColor: "#e5e7eb", textAlign: isRTL ? "right" : "left" }}>
                          {t("accountingReports.account", { defaultValue: "الحساب" })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={7} style={{ border: "1px solid #000", padding: "8px", textAlign: "center" }}>
                            {t("accountingReports.noData", { defaultValue: "لا توجد بيانات" })}
                          </td>
                        </tr>
                      )}
                      {rows.map((row, idx) => {
                        const debit = row.debit ?? row.debits ?? 0;
                        const credit = row.credit ?? row.credits ?? 0;
                        const balance = row.balance ?? row.current_balance ?? row.running_balance ?? 0;
                        return (
                          <tr key={row.id || row.entry_id || idx}>
                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>
                              {page.startIndex + idx + 1}
                            </td>
                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: isRTL ? "right" : "left" }}>
                              {formatDate(row.date || row.transaction_date)}
                            </td>
                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: isRTL ? "right" : "left" }}>
                              {row.description || row.details || "—"}
                            </td>
                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>
                              {formatNumber(debit)}
                            </td>
                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center" }}>
                              {formatNumber(credit)}
                            </td>
                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: "center", fontWeight: "bold" }}>
                              {formatNumber(balance)}
                            </td>
                            <td style={{ border: "1px solid #000", padding: "6px", textAlign: isRTL ? "right" : "left" }}>
                              {row.account_name || row.account || "—"}
                            </td>
                          </tr>
                        );
                      })}
                      {page.isLastSectionPage && (
                        <tr style={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
                          <td style={{ border: "1px solid #000", padding: "8px", textAlign: isRTL ? "right" : "left" }} colSpan={3}>
                            {t("accountingReports.total", { defaultValue: "الإجمالي" })}
                          </td>
                          <td style={{ border: "1px solid #000", padding: "8px", textAlign: "center" }}>
                            {formatNumber(totals.debit ?? totals.total_debit ?? 0)}
                          </td>
                          <td style={{ border: "1px solid #000", padding: "8px", textAlign: "center" }}>
                            {formatNumber(totals.credit ?? totals.total_credit ?? 0)}
                          </td>
                          <td style={{ border: "1px solid #000", padding: "8px", textAlign: "center" }}>
                            {formatNumber(totals.balance ?? totals.total_balance ?? 0)}
                          </td>
                          <td style={{ border: "1px solid #000", padding: "8px" }}></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "10px",
              left: "18px",
              right: "18px",
              flexShrink: 0,
            }}
          >
            <img
              src={`${publicUrl}/FooterAce.png`}
              alt="Footer"
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                borderTop: "4px double #000",
                paddingTop: "8px",
              }}
              crossOrigin="anonymous"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostCenterReport;
